import type { PurchaseOverheadGroup } from '../types/purchasing';
import { PURCHASE_OVERHEAD_GROUP_IDS, isPurchaseOverheadGroup } from './purchasingSpendClass';

export type OpexBudgetInterval = 'monthly' | 'quarterly' | 'annual';

export const OPEX_BUDGET_INTERVALS: ReadonlyArray<{
  id: OpexBudgetInterval;
  label: string;
  suffix: string;
}> = [
  { id: 'monthly', label: 'Monthly', suffix: '/mo' },
  { id: 'quarterly', label: 'Quarterly', suffix: '/qtr' },
  { id: 'annual', label: 'Annual', suffix: '/yr' },
];

export function isOpexBudgetInterval(value: unknown): value is OpexBudgetInterval {
  return value === 'monthly' || value === 'quarterly' || value === 'annual';
}

export function opexBudgetIntervalMeta(interval: OpexBudgetInterval) {
  return OPEX_BUDGET_INTERVALS.find(row => row.id === interval) || OPEX_BUDGET_INTERVALS[0];
}

export interface OpexGroupBudget {
  /** Envelope amount for `interval`. */
  amountUsd: number | null;
  interval: OpexBudgetInterval;
}

export interface OpexBudgets {
  groups: Record<PurchaseOverheadGroup, OpexGroupBudget>;
  updatedAt?: Date;
  updatedBy?: string;
}

function emptyGroup(): OpexGroupBudget {
  return { amountUsd: null, interval: 'monthly' };
}

export function emptyOpexBudgets(): OpexBudgets {
  const groups = {} as Record<PurchaseOverheadGroup, OpexGroupBudget>;
  for (const id of PURCHASE_OVERHEAD_GROUP_IDS) {
    groups[id] = emptyGroup();
  }
  return { groups };
}

export function parseOpexGroupBudget(raw: unknown): OpexGroupBudget {
  if (!raw || typeof raw !== 'object') return emptyGroup();
  const row = raw as { amountUsd?: unknown; monthlyUsd?: unknown; interval?: unknown };
  const interval = isOpexBudgetInterval(row.interval) ? row.interval : 'monthly';
  const amount = Number(row.amountUsd);
  if (Number.isFinite(amount) && amount >= 0) {
    return { amountUsd: amount, interval };
  }
  const legacyMonthly = Number(row.monthlyUsd);
  if (Number.isFinite(legacyMonthly) && legacyMonthly >= 0) {
    return { amountUsd: legacyMonthly, interval: 'monthly' };
  }
  return { amountUsd: null, interval };
}

export function parseOpexBudgets(raw: Record<string, unknown> | undefined | null): OpexBudgets {
  const parsed = emptyOpexBudgets();
  const groups = raw?.groups;
  if (groups && typeof groups === 'object') {
    const map = groups as Record<string, unknown>;
    for (const id of PURCHASE_OVERHEAD_GROUP_IDS) {
      parsed.groups[id] = parseOpexGroupBudget(map[id]);
    }
  }
  return parsed;
}

export function budgetedMonthlyEquivalent(budget: OpexGroupBudget | undefined): number | null {
  if (!budget || budget.amountUsd == null) return null;
  if (budget.interval === 'annual') return budget.amountUsd / 12;
  if (budget.interval === 'quarterly') return budget.amountUsd / 3;
  return budget.amountUsd;
}

export function groupBudgetEnvelope(
  budgets: OpexBudgets,
  group: PurchaseOverheadGroup | undefined,
): OpexGroupBudget {
  if (!group || !isPurchaseOverheadGroup(group)) return emptyGroup();
  return budgets.groups[group] || emptyGroup();
}

export function groupBudgetedMonthly(budgets: OpexBudgets, group: PurchaseOverheadGroup | undefined): number | null {
  return budgetedMonthlyEquivalent(groupBudgetEnvelope(budgets, group));
}

/** Monthly equivalent for each group that has a budget set. Unset groups are omitted. */
export function budgetedMonthlyByGroup(budgets: OpexBudgets): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of PURCHASE_OVERHEAD_GROUP_IDS) {
    const value = budgetedMonthlyEquivalent(budgets.groups[id]);
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) out[id] = value;
  }
  return out;
}

export function totalBudgetedOpexMonthly(budgets: OpexBudgets): number {
  return PURCHASE_OVERHEAD_GROUP_IDS.reduce((sum, id) => {
    const value = budgetedMonthlyEquivalent(budgets.groups[id]);
    return sum + (typeof value === 'number' && Number.isFinite(value) ? value : 0);
  }, 0);
}

export function formatOpexBudgetAmount(budget: OpexGroupBudget): string | null {
  if (budget.amountUsd == null) return null;
  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(budget.amountUsd);
  return `${money} ${opexBudgetIntervalMeta(budget.interval).suffix}`;
}
